import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useState } from 'react';
import { ClinicLayout } from './clinic/ClinicLayout';
import { ClinicSignInPage } from './clinic/ClinicSignInPage';
import { ErrorPanel } from './components/ErrorPanel';
import { FaqPage } from './components/FaqPage';
import { LoginPage } from './components/LoginPage';
import { PageLoader } from './components/PageLoader';
import { PageShell } from './components/PageShell';
import { PasswordGate } from './components/PasswordGate';
import { PortalPage } from './components/PortalPage';
import { SiteHeader } from './components/SiteHeader';
import { failureKindOf, meQuery } from './queries';
import { switchIdentity } from './queryClient';
import { trackView } from './pageEvents';
import { usePath } from './router';

function takeOauthError(): string | null {
  const reason = new URLSearchParams(window.location.search).get('login_error');
  if (reason !== null) {
    window.history.replaceState(null, '', window.location.pathname);
  }
  return reason;
}

function clinicFrame(content: ReactNode) {
  return (
    <ClinicLayout>
      {/* Re-points the portal's focus and input-edge colour to the clinic teal. */}
      <div className="[--mantine-color-brand-7:var(--color-deep)]">
        {content}
      </div>
    </ClinicLayout>
  );
}

export function App() {
  const path = usePath();
  useEffect(() => trackView(path), [path]);
  if (path === '/faq') return <FaqPage />;
  if (path === '/clinics/register/winter27')
    return (
      <PasswordGate gate="clinics-winter27" accent="deep" frame={clinicFrame}>
        <MemberApp />
      </PasswordGate>
    );
  return <MemberApp />;
}

function MemberApp() {
  const path = usePath();
  const queryClient = useQueryClient();
  const me = useQuery(meQuery());
  const [oauthError] = useState(takeOauthError);
  const loggedOut = failureKindOf(me.error) === 'unauthorized';

  if (me.data && !loggedOut) return <PortalPage data={me.data} />;
  // The clinic page signs visitors in itself.
  if (loggedOut && path === '/clinics/register/winter27')
    return <ClinicSignInPage oauthError={oauthError} />;

  let body: ReactNode;
  if (me.isPending) {
    body = <PageLoader />;
  } else if (loggedOut) {
    body = (
      <div className="mx-auto my-auto flex w-full max-w-md flex-col gap-4">
        <h2 className="font-display text-ink text-2xl font-bold tracking-wide uppercase">
          Welcome
        </h2>
        <LoginPage
          onLoggedIn={() => switchIdentity(queryClient)}
          oauthError={oauthError}
        />
      </div>
    );
  } else {
    body = (
      <div className="mx-auto my-auto w-full max-w-md">
        <ErrorPanel
          title="We couldn’t load the portal"
          message="Your connection may have dropped, or the portal may be temporarily unavailable. Your login has not been changed."
          onRetry={() => void me.refetch()}
        />
      </div>
    );
  }

  return <PageShell header={<SiteHeader />}>{body}</PageShell>;
}
